/**
 * Composite Analyzer: Experiment Comparison — A/B test grouping (Phase 16).
 *
 * Reads `trade.experiment` (a free-text label set by the trader on
 * deliberately A/B-tested trades) and groups variants — typically by
 * `atmStrategy` since that's where SL/PT differences live — to surface:
 *
 *   1. IN-PROGRESS — at least one trade in the experiment is still open.
 *      Shows the live scoreboard and a soft note that the leaderboard
 *      could shift before the test completes.
 *
 *   2. WINNER — all closed, ≥2 variants with ≥MIN_TRADES_PER_VARIANT
 *      samples each, and the leader has a meaningful gap (≥10pp WR or
 *      ≥0.5R avg). Promote to default; retire the losing variants.
 *
 *   3. UNDERSAMPLED — closed but at least one variant has fewer than
 *      MIN_TRADES_PER_VARIANT samples. Coach asks for more data before
 *      naming a winner.
 *
 *   4. INCONCLUSIVE — closed, enough samples on each side, but variants
 *      are within the noise floor (no clear leader). The trader picks
 *      based on subjective preference instead of stats.
 *
 * Trades without an `experiment` value are silently excluded — Coach
 * doesn't nag traders who don't run experiments. Variants are grouped
 * by ATM strategy because that's the parameter most A/B tests vary
 * (different stop losses, profit targets, runners). Same-ATM trades
 * within an experiment count toward the same variant.
 *
 * Reads:
 *   trade.experiment   — string, the experiment label (must match across variants)
 *   trade.atmStrategy  — string, the variant key
 *   trade.netPnl       — must be enriched by the scope
 *   trade.rMultiple    — for avg R per variant
 *   trade.isOpen       — boolean, separates pending from closed
 */

import { CATEGORY, SCOPE, SEVERITY, makeObservation } from '../../types.js';

export const key = 'experimentComparison';
export const tier = 'free';

// Sample-size guards
const MIN_VARIANTS = 2;            // need ≥2 different ATM variants to compare
const MIN_TRADES_PER_VARIANT = 2;  // need ≥2 closed trades per variant before naming a winner
const MIN_WR_GAP_FOR_WINNER = 0.10; // 10 percentage-point WR gap to call a winner
const MIN_R_GAP_FOR_WINNER = 0.5;   // 0.5R avg-R gap to call a winner

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';
const fmtUsd = (n) => {
  if (!Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '−';
  return `${sign}$${Math.abs(n).toFixed(0)}`;
};

const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;

/**
 * Variant key for grouping. ATM strategy is the natural axis since most
 * A/B tests vary stop loss / profit target via different ATM templates.
 * Empty/missing falls back to a sentinel so the analyzer doesn't crash
 * on freshly-created trades that haven't picked an ATM yet.
 */
const variantKey = (t) => {
  const atm = (t?.atmStrategy || '').trim();
  return atm || '(no ATM)';
};

/**
 * Bucket trades into variants by ATM strategy. Open trades go in a separate
 * `pending` count — their P&L isn't final so they don't contribute to stats.
 *
 * Returns:
 *   stats    — array of { name, count, wins, wr, avgR, netPnl }, sorted by
 *              WR desc, tie-broken by avgR desc, then by netPnl desc
 *   pending  — count of open trades in the experiment
 */
const bucketByVariant = (trades) => {
  const variants = new Map();
  let pending = 0;
  for (const t of trades) {
    if (t?.isOpen) { pending++; continue; }
    const k = variantKey(t);
    if (!variants.has(k)) variants.set(k, []);
    variants.get(k).push(t);
  }
  const stats = [];
  for (const [name, ts] of variants.entries()) {
    const wins = ts.filter(isWin).length;
    const wr = ts.length > 0 ? wins / ts.length : 0;
    const rs = ts.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
    const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : null;
    const netPnl = ts.reduce((s, t) => s + (Number.isFinite(t.netPnl) ? t.netPnl : 0), 0);
    stats.push({ name, count: ts.length, wins, wr, avgR, netPnl });
  }
  stats.sort((a, b) => {
    if (b.wr !== a.wr) return b.wr - a.wr;
    if ((b.avgR ?? -Infinity) !== (a.avgR ?? -Infinity)) return (b.avgR ?? -Infinity) - (a.avgR ?? -Infinity);
    return b.netPnl - a.netPnl;
  });
  return { stats, pending };
};

/**
 * Render a one-line scoreboard for the variants. Used in detail strings.
 *   "12pSL: 3t · WR 67% · +1.40R · +$280 | 15pSL: 3t · WR 33% · −0.20R · −$60"
 */
const renderScoreboard = (stats) =>
  stats
    .map(s => `${s.name}: ${s.count}t · WR ${fmtPct(s.wr)}${s.avgR != null ? ` · ${fmtR(s.avgR)}` : ''} · ${fmtUsd(s.netPnl)}`)
    .join(' | ');

export const analyze = (ctx) => {
  const { experimentName, experimentTrades } = ctx || {};
  if (!experimentName || typeof experimentName !== 'string') return null;
  if (!Array.isArray(experimentTrades) || experimentTrades.length === 0) return null;

  const { stats, pending } = bucketByVariant(experimentTrades);

  // Need at least 2 distinct variants for the comparison to make sense.
  // If everything's the same ATM, it's not really an A/B test — silent null.
  if (stats.length < MIN_VARIANTS) return null;

  // ── In-progress: at least one trade still open ────────────────────
  if (pending > 0) {
    const top = stats[0];
    return makeObservation({
      id: `${key}:${experimentName}:in-progress`,
      analyzer: key,
      scope: SCOPE.EXPERIMENT,
      scopeId: experimentName,
      severity: SEVERITY.INFO,
      category: CATEGORY.PROCESS,
      title: `Experiment "${experimentName}" in progress — ${pending} open, ${stats.length} variants`,
      detail: `${renderScoreboard(stats)}. Currently leading: ${top.name}.`,
      action: `Wait for the ${pending > 1 ? `${pending} open trades` : 'open trade'} to close before drawing conclusions — the leaderboard could shift.`,
      evidence: { variants: stats, pending, experimentName },
      confidence: 0.65,
      priority: 60,
    });
  }

  // ── Closed: see if there's enough data to call a winner ──────────
  const candidates = stats.filter(s => s.count >= MIN_TRADES_PER_VARIANT);
  if (candidates.length < MIN_VARIANTS) {
    return makeObservation({
      id: `${key}:${experimentName}:undersampled`,
      analyzer: key,
      scope: SCOPE.EXPERIMENT,
      scopeId: experimentName,
      severity: SEVERITY.INFO,
      category: CATEGORY.PROCESS,
      title: `Experiment "${experimentName}" — too few samples to call a winner`,
      detail: `${renderScoreboard(stats)}. Need ≥${MIN_TRADES_PER_VARIANT} closed trades per variant before naming a winner.`,
      action: `Keep running until each variant has ${MIN_TRADES_PER_VARIANT}+ trades — then Coach can pick the winner.`,
      evidence: { variants: stats, pending: 0, experimentName },
      confidence: 0.55,
      priority: 50,
    });
  }

  const top = candidates[0];
  const runnerUp = candidates[1];
  const wrGap = top.wr - runnerUp.wr;
  const rGap = (top.avgR ?? 0) - (runnerUp.avgR ?? 0);

  // ── Clear winner: meaningful gap in WR or avg R ──────────────────
  if (wrGap >= MIN_WR_GAP_FOR_WINNER || rGap >= MIN_R_GAP_FOR_WINNER) {
    const reasons = [];
    if (wrGap >= MIN_WR_GAP_FOR_WINNER) reasons.push(`+${Math.round(wrGap * 100)}pp WR`);
    if (rGap >= MIN_R_GAP_FOR_WINNER) reasons.push(`+${rGap.toFixed(2)}R avg`);
    return makeObservation({
      id: `${key}:${experimentName}:winner`,
      analyzer: key,
      scope: SCOPE.EXPERIMENT,
      scopeId: experimentName,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.PROCESS,
      title: `Experiment "${experimentName}" — "${top.name}" is the winner (${reasons.join(', ')})`,
      detail: `${renderScoreboard(stats)}. Winner beats runner-up "${runnerUp.name}" by ${reasons.join(' and ')}.`,
      action: `Promote "${top.name}" to the default for this setup. Retire the losing variants or run a follow-up to confirm.`,
      evidence: { variants: stats, pending: 0, experimentName, winner: top.name, wrGap, rGap },
      confidence: 0.75,
      priority: 80,
    });
  }

  // ── Inconclusive: enough samples, no meaningful gap ──────────────
  const totalCount = stats.reduce((s, v) => s + v.count, 0);
  return makeObservation({
    id: `${key}:${experimentName}:inconclusive`,
    analyzer: key,
    scope: SCOPE.EXPERIMENT,
    scopeId: experimentName,
    severity: SEVERITY.INFO,
    category: CATEGORY.PROCESS,
    title: `Experiment "${experimentName}" — no clear winner`,
    detail: `${renderScoreboard(stats)}. Variants are within ${Math.round(Math.abs(wrGap) * 100)}pp WR / ${rGap.toFixed(2)}R of each other.`,
    action: `Either run more samples (${totalCount} trades total — try 10-15+ before calling) or pick the variant you prefer for non-stat reasons.`,
    evidence: { variants: stats, pending: 0, experimentName, wrGap, rGap },
    confidence: 0.6,
    priority: 55,
  });
};

// Test-friendly exports
export {
  variantKey,
  bucketByVariant,
  renderScoreboard,
  MIN_VARIANTS,
  MIN_TRADES_PER_VARIANT,
  MIN_WR_GAP_FOR_WINNER,
  MIN_R_GAP_FOR_WINNER,
};
